import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { Diff } from "@/features/FormCard/Diff";
import { InputField } from "@/features/FormCard/Fields";
import { ConfirmationModal } from "@/features/FormCard/Modals";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { useEditForm } from "@/features/Edit/hooks";

const FormSchema = z.object({
  fund_id: z.coerce.number().nullable().optional(),
  file_name: z.string().nullable().optional(),
  reporting_date: z.string().nullable().optional(),
  reporting_period: z.string().nullable().optional(),
  processing_status: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function K1DocumentsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isUpdating,
    changedFields,
    openDeleteModal,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Document",
                })}{" "}
                #{id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="fund_id"
                    label={t(`${table_name}.columns.fund_id`, {
                      ns: "tables",
                      defaultValue: "Fund",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="file_name"
                    label={t(`${table_name}.columns.file_name`, {
                      ns: "tables",
                      defaultValue: "File name",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="reporting_date"
                    label={t(`${table_name}.columns.reporting_date`, {
                      ns: "tables",
                      defaultValue: "Reporting date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="reporting_period"
                    label={t(`${table_name}.columns.reporting_period`, {
                      ns: "tables",
                      defaultValue: "Reporting period",
                    })}
                    control={form.control}
                  />
                </div>
                <InputField
                  name="processing_status"
                  label={t(`${table_name}.columns.processing_status`, {
                    ns: "tables",
                    defaultValue: "Processing status",
                  })}
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? Diff(changedFields, [], {}, table_name)
            : "No changes"}
        </ConfirmationModal>
      </div>
    </div>
  );
}
